/**
 * Shared psychotherapy note editor. Care-team members read notes on the appointment;
 * only the episode care manager gets the editor (`allowWrite`). Admins never see them.
 */

import {
  Alert,
  Button,
  Group,
  Stack,
  Text,
  Textarea,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import type { DocumentReference, Encounter, Patient, Practitioner, Reference } from '@medplum/fhirtypes';
import { useMedplum, useMedplumProfile } from '@medplum/react-hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  findOwnPsychotherapyNoteForAppointment,
  savePsychotherapyNote,
  searchPsychotherapyNotesForAppointment,
} from '@/lib/psychotherapyNotes';
import {
  getAuthorRef,
  getPsychotherapyNoteText,
} from '@unify/fhir-model/resources/document-reference';
import { usePermissions } from '@/contexts/PermissionContext';
import { canViewPsychotherapyNote } from '@/lib/chart/chartDocumentAccess';

export interface PsychotherapyNoteSectionProps {
  appointmentId: string;
  patientRef: Reference<Patient>;
  encounterRef?: Reference<Encounter>;
  /** Compact layout for Zoom sidebar. */
  compact?: boolean;
  title?: string;
  /** When false, the editor is hidden and existing notes are shown read-only. Default true. */
  allowWrite?: boolean;
}

function isPractitionerProfile(profile: unknown): profile is Practitioner {
  return Boolean(
    profile &&
      typeof profile === 'object' &&
      'resourceType' in profile &&
      (profile as { resourceType: string }).resourceType === 'Practitioner' &&
      'id' in profile &&
      Boolean((profile as { id?: string }).id),
  );
}

function authorRefId(doc: DocumentReference): string | undefined {
  const ref = getAuthorRef(doc)?.reference;
  if (!ref?.startsWith('Practitioner/')) return undefined;
  return ref.slice('Practitioner/'.length);
}

export function PsychotherapyNoteSection({
  appointmentId,
  patientRef,
  encounterRef,
  compact = false,
  title = 'Psychotherapy note',
  allowWrite = true,
}: PsychotherapyNoteSectionProps): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile();
  const { role } = usePermissions();
  const canView = canViewPsychotherapyNote(role);
  const practitioner = isPractitionerProfile(profile) && canView ? profile : undefined;

  const [ownNote, setOwnNote] = useState<DocumentReference | null>(null);
  const [superviseeNotes, setSuperviseeNotes] = useState<DocumentReference[]>([]);
  const [draft, setDraft] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!appointmentId || !practitioner?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const all = await searchPsychotherapyNotesForAppointment(medplum, appointmentId);
      const own =
        all.find((d) => authorRefId(d) === practitioner.id) ??
        (await findOwnPsychotherapyNoteForAppointment(medplum, appointmentId, practitioner.id));
      setOwnNote(own ?? null);
      setSuperviseeNotes(all.filter((d) => authorRefId(d) !== practitioner.id));
      setDraft(own ? getPsychotherapyNoteText(own) : '');
    } catch (err) {
      console.warn('Failed to load psychotherapy notes', err);
    } finally {
      setLoading(false);
    }
  }, [appointmentId, medplum, practitioner?.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleSave = useCallback(async () => {
    if (!allowWrite || !practitioner?.id || !patientRef.reference) return;
    if (!draft.trim()) {
      return;
    }
    setSaving(true);
    try {
      const saved = await savePsychotherapyNote({
        medplum,
        existing: ownNote,
        patientRef,
        author: practitioner,
        appointmentId,
        text: draft,
        encounterRef,
      });
      setOwnNote(saved);
      setDraft(getPsychotherapyNoteText(saved));
      notifications.show({ color: 'green', message: 'Psychotherapy note saved' });
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'Save failed',
        message: err instanceof Error ? err.message : 'Could not save psychotherapy note',
      });
    } finally {
      setSaving(false);
    }
  }, [allowWrite, appointmentId, draft, encounterRef, medplum, ownNote, patientRef, practitioner]);

  const superviseeBlocks = useMemo(
    () =>
      superviseeNotes.map((doc) => {
        const author = doc.author?.[0]?.display ?? doc.author?.[0]?.reference ?? 'Supervisee';
        return (
          <Stack key={doc.id} gap={4}>
            <Text size="xs" fw={600} c="dimmed">
              {author} (read-only)
            </Text>
            <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
              {getPsychotherapyNoteText(doc) || '(Empty)'}
            </Text>
          </Stack>
        );
      }),
    [superviseeNotes],
  );

  if (!practitioner) {
    if (!canView) return <></>;
    return (
      <Alert color="gray" variant="light">
        Sign in as a provider to view psychotherapy notes.
      </Alert>
    );
  }

  return (
    <Stack gap={compact ? 'xs' : 'sm'}>
      {compact ? (
        <Text size="sm" fw={600}>
          {title}
        </Text>
      ) : (
        <Title order={5}>{title}</Title>
      )}
      {loading ? (
        <Text size="sm" c="dimmed">
          Loading…
        </Text>
      ) : null}

      {!loading ? (
        allowWrite ? (
        <Stack gap="xs">
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.currentTarget.value)}
            minRows={compact ? 3 : 4}
            placeholder="Add a private psychotherapy note…"
            autosize
          />
          <Group justify="flex-end">
            <Button
              size={compact ? 'sm' : undefined}
              loading={saving}
              onClick={() => void handleSave()}
            >
              Save
            </Button>
          </Group>
        </Stack>
        ) : draft.trim() ? (
          <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
            {draft}
          </Text>
        ) : null
      ) : null}

      {!loading && superviseeBlocks}
      {!loading && !allowWrite && !draft.trim() && superviseeNotes.length === 0 ? (
        <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
          No psychotherapy note.
        </Text>
      ) : null}
    </Stack>
  );
}
